import React from "react";
import { motion } from "framer-motion";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { initialImpact } from "../data/initialData";

const audienceItems = [
  "Governments",
  "Universities",
  "Research Institutions",
  "NGOs",
  "Startups",
  "Enterprises"
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const Impact = () => {
  const [impact] = useLocalStorage('impact', initialImpact);

  return (
    <motion.section
      id="impact"
      className="py-24 relative overflow-hidden bg-slate-900/50"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={containerVariants}
    >
      <div className="absolute inset-0 bg-grid opacity-20" />

      <motion.div
        className="absolute -bottom-40 -left-40 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-[120px]"
        animate={{ x: [0, 60, 0], y: [0, -40, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="section-container relative z-10">
        <motion.div className="text-center mb-16" variants={itemVariants}>
          <motion.span
            className="inline-block px-4 py-1.5 mb-4 text-xs font-mono tracking-wider text-cyan-400 border border-cyan-500/30 rounded-full bg-cyan-500/5"
          >
            Impact
          </motion.span>
          <motion.h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            Building AI for <span className="gradient-text">African Languages</span>
          </motion.h2>
          <motion.p className="text-slate-400 max-w-3xl mx-auto text-base">
            Empowering governments, universities, research institutions, NGOs, startups, and enterprises to build AI applications for indigenous African languages through scalable language infrastructure.
          </motion.p>
        </motion.div>

        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-16"
          variants={itemVariants}
        >
          {audienceItems.map((item) => (
            <span
              key={item}
              className="px-4 py-2 bg-slate-800/60 border border-slate-700/50 rounded-full text-sm text-slate-300"
            >
              {item}
            </span>
          ))}
        </motion.div>

        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
          variants={containerVariants}
        >
          {impact.map((area) => (
            <motion.div
              key={area.title}
              variants={itemVariants}
              className="glass-card rounded-xl p-5 hover:border-cyan-500/20 transition-all duration-300 group"
              whileHover={{ y: -3 }}
            >
              <h3 className="text-sm font-semibold text-white mb-2 group-hover:text-cyan-300 transition-colors">
                {area.title}
              </h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                {area.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Impact;
